import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  signal,
  ViewChild,
  input,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AwBadgeComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwExpansionPanelComponent,
  AwSearchComponent,
  AwSpinnerComponent,
  AwTableComponent,
  DialogFormControls,
  DialogOptions,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { GraphQLService } from '../../../core/services/graphql.service';
import { UnassignButtonComponent } from './unassign-button.component';
import { TableNameDirective } from '../../directives/table-name.directive';
import { ExpansionPanelA11yDirective } from '../../directives/expansion-panel-a11y.directive';

/**
 * Configuration interface that drives the component behavior based on mode.
 */
export interface ModeConfig {
  panelTitle: string;
  columnLabel: string;
  entityIdField: string;
  entityNameField: string;
  rowIdField: string;
  getAllEntities: () => Observable<any[]>;
  getAssignments: (orgId: string, authParty: string) => Observable<any[]>;
  createAssignments: (orgId: string, authParty: string, ids: string[]) => Observable<any[]>;
  updateAssignments: (orgId: string, authParty: string, ids: string[]) => Observable<any[]>;
  deleteAssignments: (orgId: string, authParty: string) => Observable<void>;
}

/**
 * Internal model for table rendering.
 */
export interface ApiAccessTableRow {
  entityId: string;
  entityName: string;
  assignedBadge: {
    isAssigned: boolean;
    color: string;
    label: string;
  };
}

/**
 * Pure function: merges entities with assignments to produce table rows.
 * Accepts a config to specify which fields to read from the entity and assignment objects.
 */
export function buildTableRows(
  entities: Array<{ id?: string; [key: string]: any }>,
  assignments: Array<{ [key: string]: any }>,
  config: { entityNameField: string; assignmentIdField: string }
): ApiAccessTableRow[] {
  const assignedIds = new Set(
    assignments.map((a) => a[config.assignmentIdField]).filter((id): id is string => !!id)
  );
  return entities.map((entity) => {
    const isAssigned = assignedIds.has(entity.id ?? '');
    return {
      entityId: entity.id ?? '',
      entityName: entity[config.entityNameField] ?? '',
      assignedBadge: {
        isAssigned,
        color: isAssigned ? 'success-green-700' : 'error-red',
        label: isAssigned ? 'Yes' : 'No',
      },
    };
  });
}

/**
 * Pure function: computes the union of existing and selected IDs (for Assign).
 */
export function computeAssignPayload(
  existingIds: string[],
  selectedIds: string[]
): string[] {
  return [...new Set([...existingIds, ...selectedIds])];
}

/**
 * Pure function: computes the difference of existing minus selected IDs (for Unassign).
 */
export function computeUnassignPayload(
  existingIds: string[],
  selectedIds: string[]
): string[] {
  const removeSet = new Set(selectedIds);
  return existingIds.filter((id) => !removeSet.has(id));
}

@Component({
  selector: 'app-api-access-section',
  standalone: true,
  imports: [
    CommonModule,
    AwTableComponent,
    AwSearchComponent,
    AwDialogComponent,
    AwExpansionPanelComponent,
    AwButtonDirective,
    AwSpinnerComponent,
    MatIcon,
    FormsModule,
    TableNameDirective,
    ExpansionPanelA11yDirective,
  ],
  templateUrl: './api-access-section.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./api-access-section.component.scss'],
})
export class ApiAccessSectionComponent implements OnInit {
  readonly organizationId = input.required<string>();
  readonly authorizedParty = input.required<string>();
  readonly mode = input<'read' | 'write'>('read');
  /** The table scrolls inside this height instead of growing the page. */
  readonly tableHeight = '60vh';
  /**
   * Rows rendered per lazy-load batch. Must overfill `tableHeight` (~23 rows at 56px on a tall screen), or no scrollbar
   * appears and the next batch never loads.
   */
  readonly rowBatchSize = 30;

  @Output() toastMessage = new EventEmitter<{
    message: string;
    variant: ToastVariants;
  }>();

  @ViewChild(AwTableComponent) awTableComponent!: AwTableComponent;

  config!: ModeConfig;

  isLoading = false;
  errorMessage: string | null = null;
  assignmentsExist = false;

  apiAccessTableData = signal<ApiAccessTableRow[]>([]);
  apiAccessTableDataCopy: ApiAccessTableRow[] = [];
  searchValue = '';
  checkedRows: TableRow[] = [];
  disableChangeAssignment = true;

  openChangeAssignmentDialog = false;

  currentAssignedTableIds: Set<string> = new Set();

  columnsDefinition: TableCellInput[] = [];

  changeAssignmentFormFields = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'assignmentAction',
        label: 'Change Assignment',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Assign', value: 'assign' },
            { label: 'Unassign', value: 'unassign' },
          ],
        },
        validators: {
          required: { value: true, message: 'Please select an action' },
        },
      },
    ],
  });

  changeAssignmentDialogOptions = input<DialogOptions>({
    variant: 'standard',
    title: 'Change Assignment',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  });

  constructor(private graphQLService: GraphQLService) {}

  ngOnInit(): void {
    this.config = this.resolveConfig();
    this.columnsDefinition = this.buildColumnsDefinition();
    this.loadData();
  }

  private resolveConfig(): ModeConfig {
    if (this.mode() === 'write') {
      return {
        panelTitle: 'API - Write Access',
        columnLabel: 'Screen',
        entityIdField: 'mutatableEntityId',
        entityNameField: 'entityName',
        rowIdField: 'entityId',
        getAllEntities: () => this.graphQLService.getAllMutatableEntities(),
        getAssignments: (orgId, authParty) =>
          this.graphQLService.getMutatableEntityAssignments(orgId, authParty),
        createAssignments: (orgId, authParty, ids) =>
          this.graphQLService.createMutatableEntityAssignments(orgId, authParty, ids),
        updateAssignments: (orgId, authParty, ids) =>
          this.graphQLService.updateMutatableEntityAssignments(orgId, authParty, ids),
        deleteAssignments: (orgId, authParty) =>
          this.graphQLService.deleteMutatableEntityAssignments(orgId, authParty),
      };
    }

    return {
      panelTitle: 'API - Read Access',
      columnLabel: 'Table Name',
      entityIdField: 'queryableTableId',
      entityNameField: 'tableName',
      rowIdField: 'tableId',
      getAllEntities: () => this.graphQLService.getAllQueryableTables(),
      getAssignments: (orgId, authParty) =>
        this.graphQLService.getAssignments(orgId, authParty),
      createAssignments: (orgId, authParty, ids) =>
        this.graphQLService.createAssignments(orgId, authParty, ids),
      updateAssignments: (orgId, authParty, ids) =>
        this.graphQLService.updateAssignments(orgId, authParty, ids),
      deleteAssignments: (orgId, authParty) =>
        this.graphQLService.deleteAssignments(orgId, authParty),
    };
  }

  private buildColumnsDefinition(): TableCellInput[] {
    return [
      {
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      },
      {
        type: TableCellTypes.Title,
        key: 'entityName',
        label: this.config.columnLabel,
        isExpanded: false,
        sort: true,
        align: 'left',
      },
      {
        type: TableCellTypes.Custom,
        key: 'assigned',
        label: 'Assigned',
        isExpanded: false,
        combineFields: ['assignedBadge.color', 'assignedBadge.label'],
        combineTemplate: (values: any[]) => ({
          component: AwBadgeComponent,
          componentData: { color: values[0], label: values[1] },
        }),
        align: 'center',
      },
      {
        type: TableCellTypes.Custom,
        key: 'unassign',
        label: 'Unassign',
        isExpanded: false,
        combineFields: ['assignedBadge.isAssigned'],
        combineTemplate: (values: any[], rowData?: any) => ({
          component: UnassignButtonComponent,
          componentData: {
            isAssigned: values[0],
            rowData: rowData,
            onUnassign: (row: any) => this.unassignSingleTable(row),
          },
        }),
        align: 'center',
      },
    ];
  }

  loadData(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.config
      .getAllEntities()
      .pipe(
        switchMap((entities) => {
          return this.config
            .getAssignments(this.organizationId(), this.authorizedParty())
            .pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                  this.assignmentsExist = false;
                  return of([]);
                }
                throw error;
              }),
              switchMap((assignments) => {
                this.assignmentsExist = assignments.length > 0;
                this.currentAssignedTableIds = new Set(
                  assignments
                    .map((a: any) => a[this.config.entityIdField])
                    .filter((id: any): id is string => !!id)
                );
                const rows = buildTableRows(entities, assignments, {
                  entityNameField: this.config.entityNameField,
                  assignmentIdField: this.config.entityIdField,
                });
                this.apiAccessTableData.set(rows);
                this.apiAccessTableDataCopy = rows;
                return of(null);
              })
            );
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.errorMessage =
              'The GraphQL API is not configured in this environment';
          } else {
            this.errorMessage =
              typeof error.error === 'string'
                ? error.error
                : 'Failed to fetch entities';
          }
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }

  onSearchTable(searchInput: string): void {
    this.awTableComponent?.applyFilter(searchInput);
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }): void {
    this.checkedRows = checkedRows.rows;
    this.disableChangeAssignment = this.checkedRows.length === 0;
  }

  unassignSingleTable(row: any): void {
    const entityId = row['entityId'];
    if (!entityId || !this.currentAssignedTableIds.has(entityId)) {
      return;
    }

    const newIds = [...this.currentAssignedTableIds].filter(
      (id) => id !== entityId
    );

    this.isLoading = true;
    const operation$: Observable<void> =
      newIds.length === 0
        ? this.config.deleteAssignments(
            this.organizationId(),
            this.authorizedParty()
          )
        : this.config.updateAssignments(
            this.organizationId(),
            this.authorizedParty(),
            newIds
          ).pipe(map(() => void 0));

    operation$
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.currentAssignedTableIds.delete(entityId);
          if (newIds.length === 0) {
            this.assignmentsExist = false;
          }

          const updatedRows = this.apiAccessTableDataCopy.map((r) => {
            if (r.entityId === entityId) {
              return {
                ...r,
                assignedBadge: {
                  isAssigned: false,
                  color: 'error-red',
                  label: 'No',
                },
              };
            }
            return r;
          });
          this.apiAccessTableData.set(updatedRows);
          this.apiAccessTableDataCopy = updatedRows;
          this.toastMessage.emit({
            message: 'Successfully unassigned entity',
            variant: 'success',
          });
        },
        error: () => {
          this.toastMessage.emit({
            message: 'Failed to unassign entity',
            variant: 'error',
          });
        },
      });
  }

  changeAssignmentFormSubmit(formValues: any): void {
    if (!formValues.valid) {
      this.toastMessage.emit({
        message: 'Please select an action',
        variant: 'alert',
      });
      return;
    }
    if (!this.checkedRows.length) {
      this.toastMessage.emit({
        message: 'No rows selected',
        variant: 'error',
      });
      return;
    }

    const action: string = formValues.formData.assignmentAction.value;
    const selectedTableIds: string[] = this.checkedRows
      .map((row) => row['entityId'])
      .filter((id): id is string => !!id);
    const existingIds = [...this.currentAssignedTableIds];

    let newIds: string[];
    if (action === 'assign') {
      newIds = computeAssignPayload(existingIds, selectedTableIds);
    } else {
      newIds = computeUnassignPayload(existingIds, selectedTableIds);
    }

    this.isLoading = true;

    let operation$: Observable<null>;
    if (newIds.length === 0) {
      operation$ = this.config
        .deleteAssignments(this.organizationId(), this.authorizedParty())
        .pipe(
          map(() => {
            this.assignmentsExist = false;
            return null;
          })
        );
    } else if (!this.assignmentsExist) {
      operation$ = this.config
        .createAssignments(this.organizationId(), this.authorizedParty(), newIds)
        .pipe(
          map(() => {
            this.assignmentsExist = true;
            return null;
          })
        );
    } else {
      operation$ = this.config
        .updateAssignments(this.organizationId(), this.authorizedParty(), newIds)
        .pipe(
          map(() => {
            this.assignmentsExist = true;
            return null;
          })
        );
    }

    operation$
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeAssignmentDialog = false;
          this.checkedRows = [];
          this.disableChangeAssignment = true;
        })
      )
      .subscribe({
        next: () => {
          this.currentAssignedTableIds = new Set(newIds);
          const newAssignedSet = new Set(newIds);
          const updatedRows = this.apiAccessTableDataCopy.map((r) => {
            const isAssigned = newAssignedSet.has(r.entityId);
            return {
              ...r,
              assignedBadge: {
                isAssigned,
                color: isAssigned ? 'success-green-700' : 'error-red',
                label: isAssigned ? 'Yes' : 'No',
              },
            };
          });
          this.apiAccessTableData.set(updatedRows);
          this.apiAccessTableDataCopy = updatedRows;
          this.toastMessage.emit({
            message: 'Successfully changed assignments',
            variant: 'success',
          });
        },
        error: () => {
          this.toastMessage.emit({
            message: 'Failed to change assignments',
            variant: 'error',
          });
        },
      });
  }

  dialogFormCloseHandler(): void {
    this.openChangeAssignmentDialog = false;
  }
}
